import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Textarea } from '@mlm/ui';
import { listLessonQuestions, postLessonQuestion, replyToQuestion } from '../lib/api';
import { ReportDialog } from './report-dialog';

/**
 * Lesson Q&A — questions with one reply level; instructor replies carry a badge.
 *
 * `highlightId` comes from `?question=` on a `question_answered` notification:
 * the thread it names is scrolled to and ringed once the list has loaded.
 */
export function LessonQa({
  courseId,
  lessonId,
  highlightId,
}: {
  courseId: string;
  lessonId: string;
  highlightId?: string | null;
}) {
  const qc = useQueryClient();
  const key = ['lesson-questions', courseId, lessonId];
  const q = useQuery({ queryKey: key, queryFn: () => listLessonQuestions(courseId, lessonId) });
  const rowRefs = useRef(new Map<string, HTMLLIElement>());
  useEffect(() => {
    if (!highlightId || !q.data) return;
    rowRefs.current.get(highlightId)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlightId, q.data]);
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState('');

  const ask = useMutation({
    meta: { errorMessage: "Couldn't post your question — please try again." },
    mutationFn: () => postLessonQuestion(courseId, lessonId, draft.trim()),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: key });
    },
  });
  const reply = useMutation({
    meta: { errorMessage: "Couldn't post your reply — please try again." },
    mutationFn: () => replyToQuestion(replyTo!, replyDraft.trim()),
    onSuccess: () => {
      setReplyTo(null);
      setReplyDraft('');
      qc.invalidateQueries({ queryKey: key });
    },
  });

  const rows = q.data ?? [];
  const tops = rows.filter((r) => !r.parentId);
  const repliesFor = (id: string) => rows.filter((r) => r.parentId === id);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask a question about this lesson…"
          rows={3}
          maxLength={2000}
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={() => ask.mutate()} disabled={!draft.trim() || ask.isPending}>
            Post question
          </Button>
        </div>
      </div>
      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        // Never the empty state on failure: "be the first" reads as "nobody
        // asked anything", which is a lie when the request simply failed.
        <p className="text-sm text-muted-foreground">
          Questions couldn&apos;t be loaded.{' '}
          <button onClick={() => q.refetch()} className="font-medium text-primary hover:underline">
            Retry
          </button>
        </p>
      ) : tops.length === 0 ? (
        <p className="text-sm text-muted-foreground">No questions yet — be the first.</p>
      ) : (
        <ul className="space-y-4">
          {tops.map((t) => (
            <li
              key={t.id}
              ref={(el) => {
                if (el) rowRefs.current.set(t.id, el);
                else rowRefs.current.delete(t.id);
              }}
              className={`rounded-lg border p-3 ${
                t.id === highlightId ? 'ring-2 ring-primary/40' : ''
              }`}
            >
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">
                  {t.isMine ? 'You' : (t.authorName ?? 'Learner')}
                </span>
                <span>· {new Date(t.createdAt).toLocaleDateString()}</span>
              </div>
              <p className="mt-1 whitespace-pre-line text-sm">{t.body}</p>
              {repliesFor(t.id).length > 0 && (
                <ul className="mt-3 space-y-2 border-l-2 pl-3">
                  {repliesFor(t.id).map((r) => (
                    <li key={r.id}>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">
                          {r.isMine ? 'You' : (r.authorName ?? 'Learner')}
                        </span>
                        {r.isCreatorReply && (
                          <Badge className="h-4 px-1.5 text-[10px]">Instructor</Badge>
                        )}
                        <span>· {new Date(r.createdAt).toLocaleDateString()}</span>
                        {/* Reporting your own post is noise for the queue. */}
                        {!r.isMine && (
                          <ReportDialog targetKind="question" targetId={r.id} className="ml-auto" />
                        )}
                      </div>
                      <p className="mt-0.5 whitespace-pre-line text-sm">{r.body}</p>
                    </li>
                  ))}
                </ul>
              )}
              {replyTo === t.id ? (
                <div className="mt-3 space-y-2">
                  <Textarea
                    value={replyDraft}
                    onChange={(e) => setReplyDraft(e.target.value)}
                    rows={2}
                    maxLength={2000}
                    placeholder="Write a reply…"
                  />
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setReplyTo(null)}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => reply.mutate()}
                      disabled={!replyDraft.trim() || reply.isPending}
                    >
                      Reply
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-3">
                  <button
                    className="text-xs font-medium text-primary hover:underline"
                    onClick={() => setReplyTo(t.id)}
                  >
                    Reply
                  </button>
                  {!t.isMine && <ReportDialog targetKind="question" targetId={t.id} />}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
